<template>
  <el-dialog
    v-model="visible"
    width="650px"
    :fullscreen="isMobile"
    :close-on-click-modal="false"
    class="task-dialog"
    @closed="onClosed"
  >
    <template #header>
      <div class="flex items-center justify-between w-full pr-6">
        <div class="flex items-center gap-x-2 gap-y-0.5 flex-wrap min-w-0">
          <span class="font-semibold text-base">{{ t('task') }}</span>
          <template v-if="taskMeta.externalID">
            <span class="text-sm text-gray-400">#{{ taskMeta.externalID }}</span>
            <span class="text-sm text-gray-400">{{ t('createdBy') }}</span>
            <span class="text-sm font-semibold text-gray-700">{{ taskMeta.createdByName }}</span>
          </template>
        </div>
        <div class="flex items-center gap-2 flex-shrink-0">
          <button
            v-if="taskID && !isEditing"
            @click="isEditing = true"
            class="p-1.5 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors"
            :title="t('edit')"
          >
            <Icon icon="mdi:pencil-outline" class="w-5 h-5" />
          </button>
          <button
            v-if="isEditing"
            @click="saveTask"
            :disabled="saving"
            class="flex items-center gap-2 bg-green-100 hover:bg-green-200 text-green-800 font-medium px-4 py-1.5 rounded-lg text-sm transition-colors disabled:opacity-50"
          >
            <Icon icon="mdi:content-save-outline" class="w-4 h-4" />
            {{ t('saveChanges') }}
          </button>
        </div>
      </div>
    </template>

    <div class="px-1">

      <!-- ── VIEW MODE ─────────────────────────────────────────── -->
      <template v-if="!isEditing">
        <div class="mb-5 flex items-start justify-between gap-4">
          <div class="min-w-0">
            <p class="text-xs text-gray-400 mb-1">{{ t('taskName') }}</p>
            <p class="text-base font-bold text-gray-900 break-words">{{ form.name || '—' }}</p>
          </div>

          <!-- Status dropdown -->
          <el-dropdown v-if="taskID" trigger="click" @command="changeStatus" :disabled="changingStatus">
            <button
              class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium transition-all duration-150 hover:brightness-95 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              :class="[
                STATUS_CONFIG[form.status as keyof typeof STATUS_CONFIG]?.bg,
                STATUS_CONFIG[form.status as keyof typeof STATUS_CONFIG]?.border
              ]"
              style="color: #4B5563;"
            >
              <Icon
                :icon="changingStatus ? 'mdi:loading' : STATUS_CONFIG[form.status as keyof typeof STATUS_CONFIG]?.icon"
                class="w-4 h-4"
                :class="changingStatus ? 'animate-spin' : ''"
              />
              {{ t(statusLabelKeys[form.status] || 'toDo') }}
              <Icon icon="mdi:chevron-down" class="w-4 h-4 ms-auto" />
            </button>
            <template #dropdown>
              <el-dropdown-menu style="min-width: 160px;">
                <el-dropdown-item
                  v-for="(cfg, key) in STATUS_CONFIG"
                  :key="key"
                  :command="key"
                  :class="[cfg.baseBg, cfg.hoverBg, form.status === key ? 'font-semibold ' + cfg.bg : '']"
                  style="color: #4B5563;"
                >
                  <div class="flex items-center gap-2 w-full">
                    <Icon :icon="cfg.icon" class="w-4 h-4 flex-shrink-0" />
                    {{ t(statusLabelKeys[key] || 'toDo') }}
                    <Icon v-if="form.status === key" icon="mdi:check" class="w-3.5 h-3.5 ml-auto" />
                  </div>
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>

        <div class="mb-5">
          <p class="text-xs text-gray-400 mb-1">{{ t('description') }}</p>
          <p class="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">{{ form.description || '—' }}</p>
        </div>

        <div class="mb-5">
          <p class="text-xs text-gray-400 mb-1">{{ t('dueDate') }}</p>
          <div class="flex items-center gap-1.5 text-sm text-gray-700">
            <Icon icon="mdi:calendar-outline" class="w-4 h-4 text-gray-400" />
            <span>{{ formatDateTime(form.dueDate as string | null) || '—' }}</span>
          </div>
        </div>

        <div class="mb-5">
          <p class="text-xs text-gray-400 mb-1">{{ t('assignTo') }}</p>
          <div class="flex items-center gap-2 mt-1 flex-wrap">
            <div class="flex -space-x-1">
              <div
                v-for="assignee in assigneeUsers"
                :key="assignee.id"
                class="w-7 h-7 rounded-full border-2 border-white overflow-hidden bg-blue-100 flex items-center justify-center flex-shrink-0"
              >
                <img
                  v-if="assignee.avatar"
                  :src="storagePath + '/' + assignee.avatar"
                  :alt="assignee.name"
                  class="w-full h-full object-cover"
                />
                <span v-else class="text-xs font-medium text-blue-600">
                  {{ assignee.name?.charAt(0)?.toUpperCase() || '?' }}
                </span>
              </div>
            </div>
            <span class="text-sm font-bold text-gray-900">{{ assigneeNames || '—' }}</span>
          </div>
        </div>
      </template>

      <!-- ── EDIT / CREATE MODE ────────────────────────────────── -->
      <el-form v-else ref="formRef" :model="form" :rules="formRules" label-position="top" class="w-full [&_.el-form-item]:w-full [&_.el-date-editor]:w-full [&_.el-select]:w-full [&_.el-input]:w-full">
        <el-form-item :label="t('taskName')" prop="name">
          <el-input v-model="form.name" :placeholder="t('taskName')" />
        </el-form-item>

        <el-form-item :label="t('description')">
          <el-input
            v-model="form.description"
            type="textarea"
            :rows="3"
            :placeholder="t('description')"
          />
        </el-form-item>

        <div class="flex flex-col sm:flex-row sm:items-end gap-3 mb-[18px] w-full">
          <el-form-item :label="t('dueDate')" class="flex-1 !mb-0 [&_.el-date-editor]:w-full">
            <el-date-picker
              v-model="form.dueDate"
              type="datetime"
              :placeholder="t('dueDate')"
              format="DD/MM/YYYY hh:mm A"
              value-format="YYYY-MM-DD HH:mm:ss"
            />
          </el-form-item>

          <div class="flex-1 pb-0.5">
            <p class="text-xs text-gray-600 mb-2 font-medium">{{ t('status') }}</p>
            <el-dropdown trigger="click" @command="(key: string) => form.status = key" class="w-full sm:w-[220px]">
              <button
                type="button"
                class="w-full flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium transition-all duration-150 hover:brightness-95 active:scale-95 h-[32px]"
                :class="[
                  STATUS_CONFIG[form.status as keyof typeof STATUS_CONFIG]?.bg,
                  STATUS_CONFIG[form.status as keyof typeof STATUS_CONFIG]?.border
                ]"
                style="color: #4B5563;"
              >
                <Icon :icon="STATUS_CONFIG[form.status as keyof typeof STATUS_CONFIG]?.icon" class="w-4 h-4" />
                {{ t(statusLabelKeys[form.status] || 'toDo') }}
                <Icon icon="mdi:chevron-down" class="w-4 h-4 ms-auto" />
              </button>
              <template #dropdown>
                <el-dropdown-menu style="min-width: 220px;">
                  <el-dropdown-item
                    v-for="(cfg, key) in STATUS_CONFIG"
                    :key="key"
                    :command="key"
                    :class="[cfg.baseBg, cfg.hoverBg, form.status === key ? 'font-semibold ' + cfg.bg : '']"
                    style="color: #4B5563;"
                  >
                    <div class="flex items-center gap-2 w-full">
                      <Icon :icon="cfg.icon" class="w-4 h-4 flex-shrink-0" />
                      {{ t(statusLabelKeys[key] || 'toDo') }}
                      <Icon v-if="form.status === key" icon="mdi:check" class="w-3.5 h-3.5 ml-auto" />
                    </div>
                  </el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
          </div>
        </div>

        <el-form-item :label="t('assignTo')" class="mb-6">
          <el-select
            v-model="form.assigneeIDs"
            multiple
            filterable
            :placeholder="t('assignTo')"
            class="w-full"
          >
            <el-option
              v-for="user in users"
              :key="user.id"
              :label="user.name || user.username"
              :value="user.id"
            />
          </el-select>
        </el-form-item>
      </el-form>

      <!-- ── ATTACHMENTS ───────────────────────────────────────── -->
      <div v-if="taskID || isEditing" class="border-t border-gray-200 pt-4 mt-2 mb-4">
        <TaskAttachments
          v-model:pending-files="pendingFiles"
          :taskID="taskID"
          :can-edit="!taskID || $can('update', '/hr/tasks')"
          @count-changed="onAttachmentsCountChanged"
        />
      </div>

      <!-- ── COMMENTS (always shown for existing tasks) ─────────── -->
      <template v-if="taskID">
        <div class="border-t border-gray-200 pt-4 mt-2">
          <h4 class="font-medium text-sm text-gray-700 mb-3 flex items-center gap-2">
            <Icon icon="mdi:comment-text-multiple-outline" class="w-4 h-4" />
            {{ t('comments') }}
          </h4>

          <!-- Comments list -->
          <div ref="commentsListRef" class="space-y-3 max-h-48 overflow-y-auto mb-3">
            <div
              v-for="comment in comments"
              :key="comment.id"
              class="flex gap-2 group"
            >
              <div class="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5 overflow-hidden">
                <img
                  v-if="comment.user?.avatar"
                  :src="storagePath + '/' + comment.user.avatar"
                  class="w-full h-full object-cover"
                />
                <span v-else class="text-xs font-medium text-blue-600">
                  {{ comment.user?.name?.charAt(0)?.toUpperCase() || '?' }}
                </span>
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-baseline gap-2">
                  <span class="text-xs font-medium text-gray-800">{{ comment.user?.name || '' }}</span>
                  <span class="text-xs text-gray-400">{{ formatCommentTime(comment.createdAt) }}</span>
                </div>
                <template v-if="editingCommentID === comment.id">
                  <div class="flex flex-wrap gap-2 mt-1">
                    <el-input v-model="editingCommentText" size="small" class="flex-1 min-w-[160px]" @keyup.enter="saveCommentEdit(comment.id)" />
                    <el-button size="small" type="primary" @click="saveCommentEdit(comment.id)">{{ t('save') }}</el-button>
                    <el-button size="small" @click="editingCommentID = ''">{{ t('cancel') }}</el-button>
                  </div>
                </template>
                <p v-else class="text-xs text-gray-600 mt-0.5 break-words">{{ comment.comment }}</p>
              </div>
              <div
                v-if="comment.user?.id === currentUserID"
                class="flex gap-1 md:opacity-0 md:group-hover:opacity-100 transition-opacity flex-shrink-0"
              >
                <button class="text-gray-400 hover:text-blue-500 p-1 md:p-0.5" @click="startEditComment(comment)">
                  <Icon icon="mdi:pencil-outline" class="w-3.5 h-3.5" />
                </button>
                <button class="text-gray-400 hover:text-red-500 p-1 md:p-0.5" @click="deleteComment(comment.id)">
                  <Icon icon="mdi:trash-can-outline" class="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            <div v-if="comments.length === 0" class="text-xs text-gray-400 text-center py-2">
              {{ t('noComments') }}
            </div>
          </div>

          <!-- Add comment input -->
          <div class="flex gap-2">
            <el-input
              v-model="newComment"
              :placeholder="t('writeComment')"
              size="small"
              class="flex-1"
              @keyup.enter="submitComment"
            />
            <el-button size="small" type="primary" @click="submitComment" :disabled="!newComment.trim()">
              {{ t('send') }}
            </el-button>
          </div>
        </div>
      </template>
    </div>

    <!-- Cashier PIN Dialog -->
    <el-dialog
      v-model="pinDialogOpen"
      :title="t('enterPin')"
      width="300px"
      class="max-w-[92vw]"
      append-to-body
    >
      <el-input
        v-model="cashierPin"
        type="password"
        :placeholder="t('enterPin')"
        ref="pinInputRef"
        @keyup.enter="confirmPinAndComment"
      />
      <template #footer>
        <el-button @click="pinDialogOpen = false">{{ t('cancel') }}</el-button>
        <el-button type="primary" @click="confirmPinAndComment">{{ t('confirm') }}</el-button>
      </template>
    </el-dialog>
  </el-dialog>
</template>

<script lang="ts">
import { defineComponent, ref, watch, nextTick, PropType, computed } from 'vue'
import { Icon } from '@iconify/vue'
import { useI18n } from 'vue-i18n'
import { useSettingsStore } from '@/stores/settingsStore'
import Task, { TaskComment } from '@/models/hr/Task'
import User from '@/models/user/User'
import { useHTTP } from '@/plugins/http'
import { ElMessage } from 'element-plus'
import { useMediaQuery } from '@vueuse/core'
import TaskAttachments from './TaskAttachments.vue'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
dayjs.extend(relativeTime)

const STATUS_CONFIG = {
  todo:        { icon: 'mdi:format-list-bulleted',    bg: 'bg-gray-100',   baseBg: 'bg-gray-50',    border: 'border-gray-200',   hoverBg: 'hover:bg-gray-100'   },
  in_progress: { icon: 'mdi:circle-half-full',        bg: 'bg-yellow-100', baseBg: 'bg-yellow-50',  border: 'border-yellow-200', hoverBg: 'hover:bg-yellow-100' },
  done:        { icon: 'mdi:checkbox-marked-outline', bg: 'bg-green-100',  baseBg: 'bg-green-50',   border: 'border-green-200',  hoverBg: 'hover:bg-green-100'  },
  canceled:    { icon: 'mdi:close-circle-outline',    bg: 'bg-red-100',    baseBg: 'bg-red-50',     border: 'border-red-200',    hoverBg: 'hover:bg-red-100'    },
}

export default defineComponent({
  name: 'TaskDialog',
  components: { Icon, TaskAttachments },
  props: {
    modelValue: { type: Boolean, default: false },
    mode: { type: String as PropType<'create' | 'view' | 'edit'>, default: 'create' },
    initialTask: { type: Object as PropType<Task | null>, default: null },
    users: { type: Array as PropType<User[]>, default: () => [] }
  },
  emits: ['update:modelValue', 'saved', 'commented', 'status-changed', 'attachments-changed'],
  setup(props, { emit }) {
    const { t } = useI18n({
      messages: {
        en: {
          task: 'Task', taskName: 'Task Name', description: 'Description',
          dueDate: 'Date & Time', assignTo: 'Assign to',
          saveChanges: 'Save changes', edit: 'Edit', createdBy: 'created by',
          comments: 'Comments', noComments: 'No comments yet',
          writeComment: 'Write a comment...', send: 'Send',
          save: 'Save', cancel: 'Cancel', confirm: 'Confirm', enterPin: 'Enter cashier pin',
          incorrectPin: 'Incorrect PIN', commentAdded: 'Comment added', taskNameRequired: 'Task name is required',
          toDo: 'To Do', inProgress: 'In Progress', done: 'Done', canceled: 'Canceled',
          status: 'Status',
        },
        ar: {
          task: 'مهمة', taskName: 'اسم المهمة', description: 'الوصف',
          dueDate: 'الموعد النهائي', assignTo: 'توكَل إلى',
          saveChanges: 'حفظ التغييرات', edit: 'تعديل', createdBy: 'أنشأها',
          comments: 'التعليقات', noComments: 'لا توجد تعليقات',
          writeComment: 'اكتب تعليقاً...', send: 'إرسال',
          save: 'حفظ', cancel: 'إلغاء', confirm: 'تأكيد', enterPin: 'ادخل رمز الكاشير الخاص بك',
          incorrectPin: 'رمز الكاشير غير صحيح', commentAdded: 'تم إضافة التعليق', taskNameRequired: 'اسم المهمة مطلوب',
          toDo: 'للتنفيذ', inProgress: 'قيد التنفيذ', done: 'منفَّذة', canceled: 'ملغيّة',
          status: 'الحالة',
        }
      }
    })

    const settingsStore = useSettingsStore()
    const storagePath = settingsStore.storagePath
    const http = useHTTP()
    const isMobile = useMediaQuery('(max-width: 767px)')

    const currentUserID = computed<string>(() => {
      try { return JSON.parse(localStorage.getItem('user') || '{}').id || '' } catch { return '' }
    })

    const visible = ref(props.modelValue)
    const saving = ref(false)
    const isEditing = ref(props.mode !== 'view')
    const formRef = ref()
    const taskID = ref<string | null>(null)
    const taskMeta = ref({ externalID: '', createdByName: '' })
    const comments = ref<TaskComment[]>([])
    const newComment = ref('')
    const editingCommentID = ref('')
    const editingCommentText = ref('')
    const pinDialogOpen = ref(false)
    const cashierPin = ref('')
    const pinInputRef = ref()
    const changingStatus = ref(false)
    const pendingFiles = ref<File[]>([])
    const commentsListRef = ref<HTMLElement>()

    const statusLabelKeys: Record<string, string> = {
      todo: 'toDo', in_progress: 'inProgress', done: 'done', canceled: 'canceled'
    }

    const form = ref<Omit<Partial<Task>, 'status'> & { assigneeIDs: string[]; status: string }>({
      name: '', description: '', dueDate: null, assigneeIDs: [], status: 'todo'
    })

    const formRules = {
      name: [{ required: true, message: t('taskNameRequired'), trigger: 'blur' }]
    }

    // Display-only: resolved assignee User objects and names
    const assigneeUsers = computed(() =>
      form.value.assigneeIDs
        .map(id => props.users.find(u => u.id === id))
        .filter(Boolean) as User[]
    )
    const assigneeNames = computed(() =>
      assigneeUsers.value.map(u => u.name || u.username).join(', ')
    )

    const formatDateTime = (date: string | null) => {
      if (!date) return ''
      return dayjs(date).format('DD/MM/YYYY hh:mm A')
    }

    const formatCommentTime = (date?: string | Date) => {
      if (!date) return ''
      return dayjs(date).fromNow()
    }

    const resetForm = () => {
      form.value = { name: '', description: '', dueDate: null, assigneeIDs: [], status: 'todo' }
      taskID.value = null
      taskMeta.value = { externalID: '', createdByName: '' }
      comments.value = []
      newComment.value = ''
      pendingFiles.value = []
      formRef.value?.resetFields()
      isEditing.value = props.mode !== 'view'
    }

    watch(() => props.modelValue, val => { visible.value = val })
    watch(visible, val => { emit('update:modelValue', val) })

    watch(() => props.initialTask, (task) => {
      isEditing.value = props.mode !== 'view'
      if (task) {
        taskID.value = task.id || null
        taskMeta.value = {
          externalID: (task as any).externalID || '',
          createdByName: task.createdByUser?.name || ''
        }
        // Convert API's ISO/UTC datetime to local "YYYY-MM-DD HH:mm:ss" for the picker
        let dueDate: string | null = null
        if (task.dueDate) {
          dueDate = dayjs(task.dueDate as unknown as string).format('YYYY-MM-DD HH:mm:ss')
        }
        form.value = {
          name: task.name || '',
          description: task.description || '',
          dueDate,
          assigneeIDs: task.assignees?.map(a => a.id || '') || [],
          status: task.status || 'todo'
        }
        if (task.id) loadComments(task.id)
      } else {
        resetForm()
      }
    }, { immediate: true })

    const loadComments = async (id: string) => {
      try {
        const data = await http.get<TaskComment[]>(`/hr/tasks/comments?task_id=${id}`)
        if (data) comments.value = data
      } catch (e) { console.error(e) }
    }

    const saveTask = async () => {
      if (!formRef.value) return
      try {
        await formRef.value.validate()
      } catch { return }
      saving.value = true
      try {
        const payload = { ...form.value, id: taskID.value }
        // Convert local datetime string to ISO/UTC for the backend
        if (payload.dueDate) {
          payload.dueDate = new Date(payload.dueDate).toISOString()
        }
        emit('saved', payload, [...pendingFiles.value])
        visible.value = false
      } finally { saving.value = false }
    }

    watch(pinDialogOpen, open => {
      if (open) nextTick(() => setTimeout(() => pinInputRef.value?.focus(), 50))
    })

    const submitComment = () => {
      if (!newComment.value.trim()) return
      const requirePin = settingsStore.getValue('is_cashier_pin_required') === 'true'
      if (requirePin) { cashierPin.value = ''; pinDialogOpen.value = true }
      else doAddComment('')
    }

    const doAddComment = async (pin: string): Promise<boolean> => {
      if (!taskID.value || !newComment.value.trim()) return false
      try {
        const headers: Record<string, string> = {}
        if (pin) headers.pin = pin
        const comment = await http.post<TaskComment>(
          `/hr/tasks/comments?task_id=${taskID.value}`,
          { comment: newComment.value },
          { headers }
        )
        if (comment) {
          comments.value.push(comment)
          newComment.value = ''
          ElMessage.success(t('commentAdded'))
          emit('commented', taskID.value, 1)
          nextTick(() => {
            if (commentsListRef.value) commentsListRef.value.scrollTop = commentsListRef.value.scrollHeight
          })
          return true
        }
      } catch (e: any) {
        const msg = e?.body || e?.title || t('incorrectPin')
        ElMessage.error(msg)
      }
      return false
    }

    const confirmPinAndComment = async () => {
      const ok = await doAddComment(cashierPin.value)
      if (ok) pinDialogOpen.value = false
      // On failure: keep PIN dialog open so user can retry
    }

    const startEditComment = (comment: TaskComment) => {
      editingCommentID.value = comment.id || ''
      editingCommentText.value = comment.comment || ''
    }

    const saveCommentEdit = async (id: string) => {
      try {
        const updated = await http.put<TaskComment>(`/hr/tasks/comments/${id}`, { comment: editingCommentText.value })
        if (updated) {
          const idx = comments.value.findIndex(c => c.id === id)
          if (idx !== -1) comments.value[idx] = updated
        }
        editingCommentID.value = ''
      } catch (e) { console.error(e) }
    }

    const deleteComment = async (id?: string) => {
      if (!id) return
      try {
        await http.delete(`/hr/tasks/comments/${id}`)
        comments.value = comments.value.filter(c => c.id !== id)
        emit('commented', taskID.value, -1)
      } catch (e) { console.error(e) }
    }

    const changeStatus = async (newStatus: string) => {
      if (!taskID.value || newStatus === form.value.status) return
      changingStatus.value = true
      try {
        await http.put(`/hr/tasks/${taskID.value}/status`, { status: newStatus })
        form.value.status = newStatus
        emit('status-changed', { taskId: taskID.value, newStatus })
      } catch (e) {
        console.error(e)
      } finally {
        changingStatus.value = false
      }
    }

    const onAttachmentsCountChanged = (count: number) => {
      if (taskID.value) emit('attachments-changed', { taskId: taskID.value, count })
    }

    const onClosed = () => { resetForm() }

    return {
      t, visible, form, formRef, formRules, saving, isEditing, taskID, taskMeta, comments, newComment,
      editingCommentID, editingCommentText,
      pinDialogOpen, cashierPin, pinInputRef,
      storagePath, currentUserID, assigneeUsers, assigneeNames,
      formatDateTime, formatCommentTime,
      saveTask, submitComment, confirmPinAndComment,
      startEditComment, saveCommentEdit, deleteComment, onClosed,
      STATUS_CONFIG, statusLabelKeys, changingStatus, changeStatus,
      isMobile, pendingFiles, commentsListRef, onAttachmentsCountChanged,
    }
  }
})
</script>

